/**
 * Penerjemah pesan galat: dari bahasa server ke bahasa pengguna.
 *
 * KENAPA ADA. Server menjawab dengan `{"error": "..."}` yang ditulis untuk
 * developer — "body tidak valid", "sku tidak dikenal", "service token tidak
 * valid", "gagal memotong kredit". Sebelumnya string itu diteruskan apa adanya
 * ke layar. Dua akibatnya sama-sama buruk: pengguna membaca kosakata internal
 * kami (job, sku, session, billing) yang tak berarti apa pun baginya, dan kami
 * membocorkan bentuk dalam sistem kepada siapa saja yang mau memetakannya.
 *
 * CARANYA: daftar putih, bukan penyaringan. Hanya pesan yang memang kami kenali
 * yang ditampilkan — sudah ditulis ulang jadi kalimat manusia yang menyebut
 * LANGKAH BERIKUTNYA. Pesan yang tidak ada di daftar tidak pernah lolos ke
 * layar; sebagai gantinya dipakai kalimat berdasarkan kode HTTP, lalu kalimat
 * cadangan dari pemanggil. Galat baru yang belum sempat diterjemahkan otomatis
 * aman — bukan bocor.
 *
 * Informasi yang berguna TIDAK hilang: setiap terjemahan mempertahankan sebab
 * dan jalan keluarnya (kode habis ≠ kode kedaluwarsa ≠ kode salah tulis).
 */

/** Kunci ditulis huruf kecil; pencocokan mengabaikan besar-kecil & spasi tepi. */
const terjemahan: Record<string, string> = {
  // --- Kode aktivasi & sesi ---------------------------------------------------
  'kode wajib diisi': 'Kode aktivasinya belum diisi.',
  'kode tidak ditemukan':
    'Kode ini tidak kami temukan. Periksa lagi penulisannya — huruf O dan angka 0 paling sering tertukar.',
  'kode session tidak ditemukan':
    'Kode ini tidak kami temukan. Periksa lagi penulisannya — huruf O dan angka 0 paling sering tertukar.',
  'kode sudah kedaluwarsa':
    'Masa aktif kode ini sudah lewat, jadi tidak bisa dipakai lagi. Beli akses baru untuk melanjutkan.',
  'kredit kode sudah habis':
    'Kredit pada kode ini sudah terpakai semua. Beli paket lagi untuk menambah kredit.',
  'kredit sudah habis':
    'Kredit Anda sudah terpakai semua. Beli paket lagi untuk melanjutkan.',
  'kode belum tersedia':
    'Kode aktivasinya belum terbit. Biasanya butuh beberapa detik setelah pembayaran terbaca — tunggu sebentar di halaman ini.',
  'session tidak valid': 'Sesi Anda sudah berakhir. Tukar kode aktivasinya lagi untuk melanjutkan.',
  'session tidak valid atau kedaluwarsa':
    'Sesi Anda sudah berakhir. Tukar kode aktivasinya lagi untuk melanjutkan.',
  'butuh authorization: bearer <token>':
    'Sesi Anda sudah berakhir. Tukar kode aktivasinya lagi untuk melanjutkan.',

  // --- Pembayaran -------------------------------------------------------------
  'order belum lunas': 'Pembayaran Anda belum terbaca. Selesaikan pembayarannya dulu, lalu halaman ini akan lanjut sendiri.',
  'checkout tidak ditemukan':
    'Halaman pemesanan ini sudah tidak berlaku. Mulai pesanan baru dari halaman harga.',
  'order tidak ditemukan di billing':
    'Pesanan ini tidak kami temukan lagi. Mulai pesanan baru dari halaman harga.',
  'verifikasi pembayaran tidak tersedia':
    'Pemeriksaan pembayaran sedang tidak bisa dilakukan. Coba lagi sebentar lagi; kalau pembayaran Anda sudah terpotong, hubungi kami dengan bukti transfernya.',

  // --- Program agen -----------------------------------------------------------
  //
  // Tanpa dua baris ini, penolakan yang memang kita PILIH terbaca sebagai
  // kerusakan kita sendiri: 503 jatuh ke "Server kami sedang bermasalah" dan
  // 403 jatuh ke "Sesi Anda sudah berakhir". Keduanya menyesatkan pengunjung
  // yang sebenarnya hanya belum diundang.
  'pendaftaran agen sedang ditutup':
    'Pendaftaran agen sedang ditutup. Kami membukanya bertahap lewat undangan — kirim perkenalan singkat ke hello@coreasia.id untuk masuk daftar gelombang berikutnya.',
  'kode undangan tidak valid':
    'Kode undangannya tidak cocok. Periksa lagi penulisannya, atau minta kode baru ke hello@coreasia.id.',

  // --- Unduhan berkas berbayar ------------------------------------------------
  'ebook: pagu unduhan lisensi sudah habis':
    'Lisensi ini sudah mencapai batas jumlah unduhan. Berkas yang sudah terunduh tetap milik Anda; hubungi hello@coreasia.id bila salinannya benar-benar hilang.',

  // --- Pemrosesan berkas ------------------------------------------------------
  'hasil job belum tersedia': 'Hasilnya belum selesai disiapkan. Tunggu proses ini rampung dulu.',
  'job tidak ditemukan':
    'Proses ini sudah tidak ada — hasil disimpan maksimal 24 jam lalu dihapus. Silakan proses ulang berkasnya.',
  'gagal menyusun zip': 'Hasilnya gagal dibungkus jadi ZIP. Coba proses ulang berkasnya.',
  'gagal membuat job': 'Permintaan Anda belum bisa dijalankan sekarang. Coba lagi sebentar lagi.'
}

/**
 * Daftar putih KEDUA, dicocokkan dengan `includes`, untuk pesan server yang
 * memuat angka dinamis sehingga tidak bisa dicocokkan persis.
 *
 * Sifatnya tetap daftar putih dan tetap aman: yang dicocokkan adalah penggal
 * yang KAMI tulis sendiri di sini, dan yang tampil di layar tetap kalimat kami
 * — teks server tidak pernah diteruskan.
 *
 * Kenapa perlu: rem laju alat gratis menjawab dengan pesan yang menyebut
 * batasnya ("sudah 10 berkas dalam semenit …"). Tanpa entri ini, pesan itu
 * jatuh ke kalimat umum status 429 dan tawaran BerkasKilat-nya hilang — padahal
 * yang menabrak batas ini hampir selalu orang yang sedang mengerjakan satu map
 * berkas pendaftaran, yaitu calon pembelinya sendiri.
 */
const terjemahanMengandung: Array<[penggal: string, pesan: string]> = [
  [
    'berkas dalam semenit',
    'Sudah cukup banyak berkas dalam semenit terakhir. Tunggu sekitar satu menit, ' +
      'lalu lanjutkan — tidak ada batas jumlah total. Kalau berkas Anda memang banyak, ' +
      'BerkasKilat merapikan seluruh berkas pendaftaran sekali jalan.'
  ]
]

/** Kalimat menurut kode HTTP, dipakai saat pesannya tidak dikenali. */
function menurutStatus(status: number): string | undefined {
  if (status === 401 || status === 403) {
    return 'Sesi Anda sudah berakhir. Tukar kode aktivasinya lagi untuk melanjutkan.'
  }
  if (status === 413) {
    return 'Berkasnya terlalu besar untuk dikirim. Kecilkan dulu atau kirim lebih sedikit sekaligus.'
  }
  if (status === 429) {
    return 'Terlalu banyak permintaan dalam waktu singkat. Tunggu sekitar satu menit lalu coba lagi.'
  }
  if (status >= 500) {
    return 'Server kami sedang bermasalah, bukan berkas Anda. Coba lagi beberapa saat lagi.'
  }
  return undefined
}

/** Bentuk galat $fetch yang kita perlukan — sengaja longgar. */
interface GalatFetch {
  data?: { error?: string; message?: string }
  statusCode?: number
  status?: number
  message?: string
  name?: string
}

/**
 * Ubah galat $fetch menjadi satu kalimat yang pantas dibaca pengguna.
 *
 * @param fallback kalimat khas halaman pemanggil, dipakai kalau tak ada yang
 *   lebih spesifik. Ia harus sudah ramah — di sini tidak diperiksa lagi.
 */
export function pesanRamah(err: unknown, fallback: string): string {
  const e = (err ?? {}) as GalatFetch

  const mentah = (e.data?.error || e.data?.message || '').trim()
  const kecil = mentah.toLowerCase()

  const cocok = terjemahan[kecil]
  if (cocok) return cocok

  // Cocok-sebagian, HANYA untuk penggal yang kita daftarkan sendiri di atas.
  const cocokPenggal = terjemahanMengandung.find(([penggal]) => kecil.includes(penggal))
  if (cocokPenggal) return cocokPenggal[1]

  const status = Number(e.statusCode ?? e.status ?? 0)

  // Tidak ada jawaban HTTP sama sekali = jaringan putus / server mati. Ini kasus
  // yang paling sering menimpa pengguna HP, dan kalimatnya harus menyebut
  // koneksi — bukan menyalahkan berkas mereka.
  if (!status && !mentah) {
    return 'Tidak bisa menghubungi server. Periksa koneksi internet Anda lalu coba lagi.'
  }

  return menurutStatus(status) || fallback
}
